import React from 'react';
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter, Button, Badge } from '@parsina/framework';
import { C, soft } from '../../constants/theme';
import { LTR_TEXT } from '../../i18n/I18nProvider.jsx';
import {
  CONDITION_OPTIONS, PRESENCE, PRESENCE_OPTIONS, SURFACE_STATE_OPTIONS,
  isMissing, materialsFor, surfaceColor, surfacesFor,
} from '../../lib/teeth.js';
import { buildToothEvents } from '../../lib/toothDraft.js';

// Editing one tooth.
//
// THE DRAFT MODEL: nothing is sent until «ثبت». A dentist charting a molar
// touches several surfaces in a row, and a request per tap would produce a
// burst of writes the server has to reconcile — and would make «انصراف»
// meaningless. Edits accumulate as a draft and go out as ONE batch, which is
// also exactly what the server's all-or-nothing RPC expects: one clinical act,
// one transaction.
//
// A pill row is used rather than a <select> because charting is repetitive and
// one tap beats open-scroll-pick every time.

const Section = ({ title, hint, children }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
      <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.textPrimary }}>{title}</h4>
      {hint && <span style={{ fontSize: 11, color: C.textSecondary }}>{hint}</span>}
    </div>
    {children}
  </div>
);

/**
 * Read-only mode, as context rather than a prop threaded through six call
 * sites.
 *
 * Every editable control in this drawer is a `Pill`, so making Pill itself
 * consult the mode means a control added later is read-only by DEFAULT — the
 * failure mode is "a new pill does nothing on a closed visit", not "a new pill
 * silently writes to one".
 */
const ReadOnlyContext = React.createContext(false);

const Pill = ({ active, onClick, children, tone }) => {
  const readOnly = React.useContext(ReadOnlyContext);
  return (
    <button
      type="button"
      onClick={readOnly ? undefined : onClick}
      disabled={readOnly}
      aria-pressed={active}
      style={{
        border: `1.5px solid ${active ? (tone || C.teal) : C.border}`,
        background: active ? soft(tone || C.teal, 0.08) : '#fff',
        color: active ? (tone || C.teal) : C.textPrimary,
        borderRadius: 999,
        padding: '6px 12px',
        fontSize: 12.5,
        fontWeight: active ? 700 : 500,
        fontFamily: 'inherit',
        cursor: readOnly ? 'default' : 'pointer',
        opacity: readOnly && !active ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
};

const PillRow = ({ children }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{children}</div>
);

export default function ToothDrawer({
  open, fdi, state, onClose, onSave, saving, focusSurface, t,
  /**
   * A draft to open WITH, rather than the empty one.
   *
   * The one caller is a confirmed AI image finding: ticking «تأیید» on a
   * hotspot opens this drawer with the tooth, the aspect and the value already
   * chosen, and the dentist presses ثبت — or changes it first, which is the
   * entire point of routing a model proposal through the editor instead of
   * writing it to the chart directly.
   *
   * It is a SEED and not a value: the drawer owns the draft from the moment it
   * opens, so anything typed afterwards wins and a re-render cannot reset it.
   */
  seed = null,
  /**
   * Open the tooth for READING only — the drawer still shows everything that
   * has been recorded, and offers no way to add to it.
   *
   * The visit workspace passes this once the appointment is completed or
   * cancelled (dent-visit.md's read-only state): the dentist app cannot reopen
   * a finished visit, so charting into one would file a finding under a day
   * that is over, with nothing on screen afterwards to say it arrived late.
   *
   * It hides the COMMIT, not the record. A closed visit's chart is still the
   * patient's chart and must stay readable.
   */
  readOnly = false,
  /**
   * What has already happened to this tooth, rendered above the editor.
   *
   * A NODE rather than data, because the two callers of this drawer disagree
   * about what the trail costs: the patient record has the treatments in hand
   * and reads the events lazily behind a toggle, and the visit shows its own
   * trail outside the drawer entirely. The drawer stays ignorant of both and
   * only decides WHERE it goes — first, because «what was done here» is the
   * context for what is about to be recorded.
   */
  history = null,
}) {
  // draft: { presence, condition:{value,material}, surfaces:{ [code]:{value,material} }, note }
  const [draft, setDraft] = React.useState({ surfaces: {} });
  const [activeSurface, setActiveSurface] = React.useState(null);
  const [err, setErr] = React.useState(null);

  // Reset whenever the drawer opens on a different tooth — a draft carried
  // between teeth would record a filling on whichever one was opened next.
  //
  // `seed` is spread over the empty draft rather than replacing it, so a seed
  // that names only a condition still arrives with the `surfaces: {}` every
  // handler below indexes into.
  React.useEffect(() => {
    if (open) {
      setDraft({ surfaces: {}, ...(seed || {}) });
      setErr(null);
      setActiveSurface(focusSurface || null);
    }
  }, [open, fdi, focusSurface, seed]);

  // These run even while the drawer is CLOSED — see the hook-order note at
  // the early return below — so every one of them tolerates a null tooth.
  const surfaces = state?.surfacesAvailable?.length
    ? state.surfacesAvailable
    : (fdi ? surfacesFor(fdi) : []);
  const missing = isMissing(draft.presence ?? state?.presence);
  const currentOf = (code) =>
    (state?.surfaces || []).find((s) => s.surface === code)?.value || null;

  const setSurface = (code, value) => setDraft((d) => ({
    ...d,
    surfaces: {
      ...d.surfaces,
      // Tapping the chosen value again clears the pending edit rather than
      // recording the same thing twice.
      [code]: d.surfaces[code]?.value === value ? undefined : { value, material: null },
    },
  }));

  const setSurfaceMaterial = (code, material) => setDraft((d) => ({
    ...d,
    surfaces: { ...d.surfaces, [code]: { ...d.surfaces[code], material } },
  }));

  // The batch actually sent. Built here so the footer can show a count and
  // disable «ثبت» when there is nothing to save.
  //
  // `buildToothEvents` is in lib/toothDraft.js because the MOBILE tooth sheet
  // emits the same batch from a completely different form. The markup forks;
  // what goes on the wire does not (§1.1).
  const events = React.useMemo(
    () => buildToothEvents(draft, fdi, state),
    [draft, fdi, state],
  );

  // EVERY HOOK IS ABOVE THIS LINE, and must stay there.
  //
  // This return used to sit before the useMemo below, which changed the hook
  // COUNT between a closed and an open render — React error #310, thrown the
  // first time a tooth was clicked. React identifies hooks by call order, so a
  // return between two of them is never safe, however obvious the condition
  // looks.
  if (!open || !fdi) return null;

  const submit = async () => {
    setErr(null);
    const failure = await onSave(events);
    if (failure) setErr(failure);
    else onClose();
  };

  return (
    <ReadOnlyContext.Provider value={readOnly}>
    <Drawer open={open} onClose={onClose}>
      {/* The editor is reached from three places now — the arch, the mobile
          sheet, and a confirmed AI finding in فایل‌ها — and a test that wants
          to know WHICH tooth it opened on cannot ask the page: an FDI number
          appears on the arch, on the file card and in here at once. */}
      <div data-testid="tooth-drawer" data-tooth={fdi} style={{ display: 'contents' }}>
      <DrawerHeader onClose={onClose}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 17, fontWeight: 800, color: C.navy }}>
            {t('chart.tooth')} <span {...LTR_TEXT}>{fdi}</span>
          </span>
          {state?.universal && (
            <span style={{ fontSize: 11.5, color: C.textSecondary }}>
              {t('chart.universalShort')} <span {...LTR_TEXT}>{state.universal}</span>
            </span>
          )}
          {state?.isCharted === false && <Badge>{t('chart.notCharted')}</Badge>}
        </div>
      </DrawerHeader>

      <DrawerBody>
        {history}

        <Section title={t('chart.presence.title')}>
          <PillRow>
            {PRESENCE_OPTIONS.map((p) => {
              const active = (draft.presence ?? state?.presence) === p;
              return (
                <Pill key={p} active={active}
                      tone={p === PRESENCE.PRESENT ? C.teal : '#64748b'}
                      onClick={() => setDraft((d) => ({ ...d, presence: p }))}>
                  {t(`chart.presence.${p}`)}
                </Pill>
              );
            })}
          </PillRow>
        </Section>

        {/* A tooth that is not in the mouth has no surfaces to fill. Hiding the
            editor is not cosmetic — the server rejects such an event, so
            offering it would only produce an error the dentist cannot act on. */}
        {!missing && (
          <>
            <Section title={t('chart.surfaces.title')} hint={t('chart.surfaces.hint')}>
              <PillRow>
                {surfaces.map((code) => {
                  const pending = draft.surfaces[code]?.value;
                  const current = currentOf(code);
                  const shown = pending || current;
                  return (
                    <Pill key={code}
                          active={activeSurface === code}
                          tone={shown ? surfaceColor(shown).stroke : undefined}
                          onClick={() => setActiveSurface(activeSurface === code ? null : code)}>
                      <span {...LTR_TEXT}>{code}</span>
                      {' · '}
                      {t(`chart.surface.${code}`)}
                      {shown && (
                        <span style={{ marginInlineStart: 6, fontSize: 11, opacity: 0.85 }}>
                          {t(`chart.surfaceState.${shown}`)}
                          {pending && ' *'}
                        </span>
                      )}
                    </Pill>
                  );
                })}
              </PillRow>

              {activeSurface && (
                <div style={{
                  marginTop: 10, padding: 12, borderRadius: 10,
                  background: '#f8fafc', border: `1px solid ${C.border}`,
                }}>
                  <div style={{ fontSize: 12, color: C.textSecondary, marginBottom: 8 }}>
                    {t('chart.surfaces.setFor')} <b>{t(`chart.surface.${activeSurface}`)}</b>
                  </div>
                  <PillRow>
                    {SURFACE_STATE_OPTIONS.map((v) => (
                      <Pill key={v}
                            active={draft.surfaces[activeSurface]?.value === v}
                            tone={surfaceColor(v).stroke}
                            onClick={() => setSurface(activeSurface, v)}>
                        {t(`chart.surfaceState.${v}`)}
                      </Pill>
                    ))}
                  </PillRow>

                  {materialsFor('surface', draft.surfaces[activeSurface]?.value).length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 12, color: C.textSecondary, marginBottom: 6 }}>
                        {t('chart.material')}
                      </div>
                      <PillRow>
                        {materialsFor('surface', draft.surfaces[activeSurface]?.value).map((m) => (
                          <Pill key={m}
                                active={draft.surfaces[activeSurface]?.material === m}
                                onClick={() => setSurfaceMaterial(activeSurface, m)}>
                            {t(`chart.materialName.${m}`)}
                          </Pill>
                        ))}
                      </PillRow>
                    </div>
                  )}
                </div>
              )}
            </Section>

            <Section title={t('chart.condition.title')} hint={t('chart.condition.hint')}>
              <PillRow>
                {CONDITION_OPTIONS.map((c) => (
                  <Pill key={c}
                        active={draft.condition?.value === c}
                        onClick={() => setDraft((d) => ({
                          ...d,
                          condition: d.condition?.value === c ? null : { value: c, material: null },
                        }))}>
                    {t(`chart.conditionName.${c}`)}
                  </Pill>
                ))}
              </PillRow>
              {materialsFor('condition', draft.condition?.value).length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, color: C.textSecondary, marginBottom: 6 }}>
                    {t('chart.material')}
                  </div>
                  <PillRow>
                    {materialsFor('condition', draft.condition?.value).map((m) => (
                      <Pill key={m}
                            active={draft.condition?.material === m}
                            onClick={() => setDraft((d) => ({
                              ...d, condition: { ...d.condition, material: m },
                            }))}>
                        {t(`chart.materialName.${m}`)}
                      </Pill>
                    ))}
                  </PillRow>
                </div>
              )}
            </Section>
          </>
        )}

        <Section title={t('chart.note.title')}>
          <textarea
            value={draft.note || ''}
            onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
            rows={2}
            placeholder={t('chart.note.placeholder')}
            style={{
              width: '100%', padding: 10, borderRadius: 8,
              border: `1px solid ${C.border}`, fontFamily: 'inherit',
              fontSize: 13, resize: 'vertical', boxSizing: 'border-box',
            }}
          />
        </Section>

        {/* Historical vs done-here. Without it, charting a new patient's
            existing work looks like a day of treatment. */}
        <label style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 12.5, color: C.textSecondary, cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={!!draft.isHistorical}
            onChange={(e) => setDraft((d) => ({ ...d, isHistorical: e.target.checked }))}
          />
          {t('chart.isHistorical')}
        </label>

        {err && (
          <div role="alert" style={{
            marginTop: 14, padding: 10, borderRadius: 8,
            background: '#fef2f2', border: '1px solid #fecaca',
            color: '#b91c1c', fontSize: 12.5, lineHeight: 1.8,
          }}>
            {err.message}
          </div>
        )}
      </DrawerBody>

      <DrawerFooter>
        <Button variant="outline" onClick={onClose} disabled={saving}>
          {readOnly ? t('common.actions.close') : t('common.actions.cancel')}
        </Button>
        {/* No commit at all in read-only, rather than a disabled one: a greyed
            «ثبت» invites the dentist to work out why it will not press. */}
        {!readOnly && (
          <Button onClick={submit} disabled={saving || events.length === 0}>
            {saving
              ? t('common.actions.saving')
              : `${t('common.actions.save')}${events.length ? ` (${events.length})` : ''}`}
          </Button>
        )}
      </DrawerFooter>
      </div>
    </Drawer>
    </ReadOnlyContext.Provider>
  );
}
